import { Box, Grid, Typography } from '@mui/material';
import { TranslateFunction } from '../../../../hooks/useTranslate';
import { DrawerHeader } from '../../../../components/Drawer/DrawerHeader';
import { AssignPreventorCardContainer } from '../AssingPreventorCardContainer';
import SearchToolbar from '../../../../components/SearchToolbar/SearchToolbar';
import useAssignPreventor from './hooks/useAssignPreventor';
import SearchPreventorFilters from '../SearchPreventorFilters';
import { Visita } from '../../consts/visitasTypes';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import ButtonActions from '../../../../components/ButtonActions';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import Input from '../../../../components/Input/Input';
import { formatFullName } from '../../../../utils/common';

interface AssignPreventorDrawerProps {
    selected: Visita | null;
    closeDrawer: () => void;
    onSuccess?: () => void;
    translate: TranslateFunction;
    isMassiveAssignation?: boolean;
    selectedVisitsIds?: number[];
    isReassignation?: boolean;
    clearVisitsSelection: () => void;
}

const AssignPreventorDrawer = ({
    selected,
    closeDrawer,
    onSuccess,
    translate,
    isMassiveAssignation = false,
    selectedVisitsIds = [],
    isReassignation = false,
    clearVisitsSelection
}: AssignPreventorDrawerProps) => {
    const {
        searchPreventorFilters,
        providersOptions,
        selectedProvider,
        isProviderFilterLocked,
        isFetchingProviders,
        preventorsOptions,
        isFetchingPreventors,
        assignPreventorRegion,
        selectedPreventor,
        observacion,
        reassignationMotive,
        handleSelectPreventor,
        handleChangeObservation,
        handleChangeReassignationMotive,
        handleSubmit,
        isAssigningPreventor,
        isReassigningPreventor,
        handleClearFilters
    } = useAssignPreventor({
        isMassiveAssignation: isMassiveAssignation,
        singleSelectedVisit: selected,
        closeDrawer: closeDrawer,
        onSuccess,
        selectedVisitsIds: selectedVisitsIds,
        isReassignation: isReassignation,
        clearVisitsSelection: clearVisitsSelection
    });

    const hasSelectedPreventor = Boolean(selectedPreventor?.idUsuario);

    const isValidReassignationMotive =
        !isReassignation || (reassignationMotive.length >= 10 && reassignationMotive.length <= 300);

    const isConfirmDisabled = !hasSelectedPreventor || !isValidReassignationMotive;

    const MIN_REASSIGNATION_MOTIVE_LENGTH = 10;
    const MAX_REASSIGNATION_MOTIVE_LENGTH = 300;

    const currentMotiveLength = reassignationMotive.length;

    const remainingCharacters =
        currentMotiveLength < MIN_REASSIGNATION_MOTIVE_LENGTH
            ? MIN_REASSIGNATION_MOTIVE_LENGTH - currentMotiveLength
            : 0;
    const isBelowMinLength = currentMotiveLength < MIN_REASSIGNATION_MOTIVE_LENGTH;
    const isAboveMaxLength = currentMotiveLength > MAX_REASSIGNATION_MOTIVE_LENGTH;
    const isInvalidLength = isBelowMinLength || isAboveMaxLength;

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            padding={1}
            sx={{ flexDirection: 'column', height: '100%' }}
        >
            <Grid>
                <DrawerHeader
                    onIconClick={closeDrawer}
                    text={translate(
                        isReassignation
                            ? `drawers.assignPreventor.titles.reassign`
                            : `drawers.assignPreventor.titles.assign`
                    )}
                />
                {isMassiveAssignation && (
                    <Typography fontWeight={700} mt={2}>
                        {translate('drawers.assignPreventor.massive.selectedCount', {
                            count: selectedVisitsIds.length
                        })}
                    </Typography>
                )}
                <Typography fontSize={'1rem'} variant="subtitle1" mt={2}>
                    {translate(`drawers.assignPreventor.selectPreventor`)}
                </Typography>
                <AssignPreventorCardContainer>
                    <SearchToolbar
                        inputSearchName=""
                        customTitle="Filtrar preventor"
                        onChange={searchPreventorFilters.debounceSearch}
                        onClick={searchPreventorFilters.handleSubmitSearch}
                        showSearchInput={false}
                        clearFilters={handleClearFilters}
                    >
                        <SearchPreventorFilters
                            extraFilters={searchPreventorFilters.extraFilters}
                            handleChangeExtraFilters={
                                searchPreventorFilters.handleChangeExtraFilters
                            }
                            setExtraFilters={searchPreventorFilters.setExtraFilters}
                            translate={translate}
                            providerOptions={providersOptions}
                            selectedProvider={selectedProvider}
                            isProviderFilterLocked={isProviderFilterLocked}
                            isFetchingProviders={isFetchingProviders}
                            assignPreventorRegion={assignPreventorRegion}
                        />
                    </SearchToolbar>
                </AssignPreventorCardContainer>
                <Grid item xs={12}>
                    <AutocompleteComponent
                        value={selectedPreventor || null}
                        options={preventorsOptions || []}
                        size="small"
                        name="preventor"
                        required
                        loading={isFetchingPreventors}
                        label={translate('drawers.assignPreventor.input.preventor')}
                        onChange={(value: any) => {
                            handleSelectPreventor(value);
                        }}
                        templateLabel={(option: any) =>
                            formatFullName(option?.nombre, option?.apellido)
                        }
                        clearable
                    />
                </Grid>

                {selected?.motivoFalla && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {translate('drawers.assignPreventor.input.failedVisitReason')}
                        </Typography>
                        <Typography variant="body2">{selected.motivoFalla.descripcion}</Typography>
                    </Grid>
                )}

                {isReassignation && (
                    <Grid item xs={12}>
                        <Input
                            label={`${translate(
                                'drawers.assignPreventor.input.reassignationMotive'
                            )}`}
                            size="small"
                            name="reassignationMotive"
                            required
                            onChange={handleChangeReassignationMotive}
                            inputProps={{
                                minLength: MIN_REASSIGNATION_MOTIVE_LENGTH,
                                maxLength: MAX_REASSIGNATION_MOTIVE_LENGTH
                            }}
                            value={reassignationMotive || ''}
                        />

                        <Typography
                            variant="caption"
                            sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}
                        >
                            {isBelowMinLength
                                ? `Mínimo ${MIN_REASSIGNATION_MOTIVE_LENGTH} carácteres - ${currentMotiveLength}/${MAX_REASSIGNATION_MOTIVE_LENGTH}`
                                : `${currentMotiveLength}/${MAX_REASSIGNATION_MOTIVE_LENGTH} caracteres`}
                        </Typography>
                    </Grid>
                )}
            </Grid>
            <Grid
                container
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ marginTop: '1rem' }}
            >
                <Box
                    sx={{
                        '& > :not(style)': { m: 1 },
                        display: 'flex'
                    }}
                >
                    <ButtonActions
                        handleClose={closeDrawer}
                        renderBackAction
                        returnText={translate('drawers.assignPreventor.buttons.cancel')}
                        confirmText={translate(
                            isReassignation
                                ? 'drawers.assignPreventor.buttons.reassign'
                                : 'drawers.assignPreventor.buttons.assign'
                        )}
                        loading={false}
                        disabled={isConfirmDisabled}
                        onClick={handleSubmit}
                        borderRadius="4px"
                        size="small"
                    />
                </Box>
            </Grid>
            <BackdropComponent loading={isAssigningPreventor || isReassigningPreventor} />
        </Box>
    );
};

export { AssignPreventorDrawer };
